// PageDescription: 매거진 에디터 상세 페이지
import { SIX_HOURS_IN_MILLISECONDS } from '@/constants/common';
import { ExtendedQueryClient } from '@/models/common/query-client';
import { ssrQueryCache } from '@/models/common/ssr-cache';
import { Client, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { GraphqlError, ServerError } from '@/types/error';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import useGetMagazineAuthor from '@/views/magazine-author-detail/hooks/useGetMagazineAuthor';
import useGetMagazineByAuthorPublicId from '@/views/magazine-author-detail/hooks/useGetMagazineByAuthorPublicId';
import MagazineAuthorDetailView from '@/views/magazine-author-detail/ui/MagazineAuthorDetailView';
import { dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';

interface Props extends CustomPageProps {
  client: Client;
  id: string;
}

const MagazineAuthorDetailPage = ({ client }: Props) => {
  return (
    <>
      <NextSeo
        title={'건강 매거진 - 닥터나우'}
        description={'건강에 대한 궁금증을 닥터나우만의 전문 콘텐츠로 해결해 보세요.'}
      />
      <MagazineAuthorDetailView client={client} />
    </>
  );
};

export default MagazineAuthorDetailPage;

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (ctx) => {
  const id = ctx.params?.id as string;
  const page = (ctx.query?.page as string) ?? '1';

  if (!id) {
    return {
      redirect: {
        destination: ROUTES.MAGAZINE.LIST.path + '/INTERNAL',
        permanent: true,
      },
    };
  }

  const queryClient = new ExtendedQueryClient();

  const cacheKey = ctx.req.url as string;

  try {
    if (ssrQueryCache.has(cacheKey)) {
      queryClient.setQueryDataFromCache(ssrQueryCache.get(cacheKey) ?? []);

      return {
        props: {
          id,
          dehydratedState: dehydrate(queryClient),
        },
      };
    }

    await Promise.allSettled([
      useGetMagazineByAuthorPublicId.prefetchQuery(queryClient, {
        authorPublicId: id,
        pagingRequest: {
          page: Number(page),
          limit: 10,
        },
      }),
      useGetMagazineAuthor.prefetchQuery(queryClient, id),
    ]);

    ssrQueryCache.set(
      cacheKey,
      queryClient.getQueryClientData([
        useGetMagazineByAuthorPublicId.getKey({
          authorPublicId: id,
          pagingRequest: { page: Number(page), limit: 10 },
        }),
        useGetMagazineAuthor.getKey(id),
      ]),
      SIX_HOURS_IN_MILLISECONDS
    );

    return {
      props: {
        id,
        dehydratedState: dehydrate(queryClient),
      },
    };
  } catch (e) {
    const { code } = e as unknown as GraphqlError;
    if (code === ServerError.NotFound) {
      return {
        notFound: true,
      };
    }
    if (ctx.res && code === ServerError.BadRequest) {
      ctx.res.statusCode = 400;
    }

    return {
      notFound: true,
    };
  }
});
